import React, { useEffect, useState } from "react";
import {
  LuUsers,
  LuUserPlus,
  LuUserCheck,
  LuLoader,
  LuCircleDot,
  LuCircleCheck,
  LuCircleX,
  LuUserX,
  LuAlarmClock,
  LuCalendarClock,
  LuPercent,
  LuBanknote,
  LuWallet,
  LuChevronDown,
  LuChevronUp,
} from "react-icons/lu";
import * as apicall from "../../components/Apicall";
import KpiTileGrid from "../../components/Dashboard/KpiTileGrid";
import { statusMeta, CHANNEL_LABELS } from "./leadMeta";

// Same shape as the old STATUS_META lookup, but safe for any status key.
const STATUS_META = new Proxy({}, { get: (_, key) => statusMeta(String(key)) });

// KPI tiles for Lead Management, for the same leads and date range the table below shows
// (GET leads/summary). Clicking a status tile filters the table to that status.
// Drafts (pending bookings that have no lead yet) are not leads, so they are not counted here.

const ICON = { fontSize: "20px" };
const BOOKED_STATUSES = ["Booked", "WON"];
const LOST_STATUSES = ["Closed", "Closed – Invalid Number", "LOST", "LOST_STALE"];
const BREAKDOWN_KEY = "leadKpiBreakdownOpen";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const readOpen = () => {
  try {
    return window.localStorage.getItem(BREAKDOWN_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const BreakdownTable = ({ title, rows, nameKey, labelFor }) => (
  <div style={{ flex: "1 1 320px", minWidth: 0 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{title}</div>
    {rows.length === 0 ? (
      <div style={{ fontSize: 12.5, color: "#9CA3AF" }}>No leads in this range</div>
    ) : (
      <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: "#6B7280", textAlign: "right" }}>
            <th style={{ textAlign: "left", fontWeight: 500, padding: "4px 6px 4px 0" }} />
            <th style={{ fontWeight: 500, padding: "4px 6px" }}>Total</th>
            <th style={{ fontWeight: 500, padding: "4px 6px" }}>Open</th>
            <th style={{ fontWeight: 500, padding: "4px 6px" }}>Won</th>
            <th style={{ fontWeight: 500, padding: "4px 0 4px 6px" }}>Lost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[nameKey] ?? "none"} style={{ borderTop: "1px solid #F3F4F6", textAlign: "right", color: "#111827" }}>
              <td style={{ textAlign: "left", padding: "5px 6px 5px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }}>
                {labelFor(r)}
              </td>
              <td style={{ padding: "5px 6px", fontWeight: 600 }}>{r.total}</td>
              <td style={{ padding: "5px 6px" }}>{r.open_count}</td>
              <td style={{ padding: "5px 6px", color: "#047857" }}>{r.won}</td>
              <td style={{ padding: "5px 0 5px 6px", color: "#B91C1C" }}>{r.lost}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const LeadKpiStrip = ({ startDate, endDate, refreshKey, statusFilter, onStatusFilter, rightAction }) => {
  const [summary, setSummary] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(readOpen);

  useEffect(() => {
    let cancelled = false;
    apicall
      .get(`leads/summary?startDate=${startDate}&endDate=${endDate}`)
      .then((res) => {
        if (!cancelled) setSummary(res?.success ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, refreshKey]);

  const toggleBreakdown = () => {
    setShowBreakdown((v) => {
      try {
        window.localStorage.setItem(BREAKDOWN_KEY, v ? "0" : "1");
      } catch (e) {
        // remembering the choice is a convenience only
      }
      return !v;
    });
  };

  const s = summary;
  const by = s?.by_status || {};

  // A status tile: shows the count, and (when clicked) filters the table to those statuses or clears again.
  const statusTile = (key, statuses, label, icon, color, value) => {
    const active = sameSet(statusFilter, statuses);
    return {
      key,
      label,
      icon,
      color,
      value: s ? value : null,
      weekKey: key,
      active,
      onClick: () => onStatusFilter(active ? [] : statuses),
    };
  };

  const pipeline = [
    {
      key: "total",
      label: "Total leads",
      icon: <LuUsers style={{ ...ICON, color: "#037FE8" }} />,
      color: "#037FE8",
      value: s ? s.total : null,
      weekKey: "total",
      active: statusFilter.length === 0,
      onClick: () => onStatusFilter([]),
    },
    statusTile("NEW", ["NEW"], STATUS_META.NEW.label, <LuUserPlus style={{ ...ICON, color: STATUS_META.NEW.dot }} />, STATUS_META.NEW.dot, by.NEW),
    statusTile("ASSIGNED", ["ASSIGNED"], STATUS_META.ASSIGNED.label, <LuUserCheck style={{ ...ICON, color: STATUS_META.ASSIGNED.dot }} />, STATUS_META.ASSIGNED.dot, by.ASSIGNED),
    statusTile("IN_PROGRESS", ["IN_PROGRESS"], STATUS_META.IN_PROGRESS.label, <LuLoader style={{ ...ICON, color: STATUS_META.IN_PROGRESS.dot }} />, STATUS_META.IN_PROGRESS.dot, by.IN_PROGRESS),
    statusTile("OPEN", ["OPEN"], STATUS_META.OPEN.label, <LuCircleDot style={{ ...ICON, color: STATUS_META.OPEN.dot }} />, STATUS_META.OPEN.dot, by.OPEN),
    statusTile("Booked", BOOKED_STATUSES, "Booked", <LuCircleCheck style={{ ...ICON, color: STATUS_META.Booked.dot }} />, STATUS_META.Booked.dot, by.Booked),
    statusTile("Closed", LOST_STATUSES, "Closed", <LuCircleX style={{ ...ICON, color: STATUS_META.Closed.dot }} />, STATUS_META.Closed.dot, by.Closed),
  ];

  // Needs attention, and how the pipeline is converting. Plain figures: the table has no filter for them.
  const attention = [
    { key: "unassigned", label: "Unassigned (open)", icon: <LuUserX style={{ ...ICON, color: "#6C757D" }} />, color: "#6C757D", value: s ? s.unassigned : null, weekKey: null },
    { key: "overdue", label: "Follow-up overdue", icon: <LuAlarmClock style={{ ...ICON, color: "#C9194A" }} />, color: "#C9194A", value: s ? s.overdue : null, weekKey: null },
    { key: "due_today", label: "Follow-up due today", icon: <LuCalendarClock style={{ ...ICON, color: "#FF8C00" }} />, color: "#FF8C00", value: s ? s.due_today : null, weekKey: null },
    {
      key: "conversion",
      label: "Conversion (won / decided)",
      icon: <LuPercent style={{ ...ICON, color: "#4F46E5" }} />,
      color: "#4F46E5",
      value: s ? (s.conversion_pct == null ? "—" : `${s.conversion_pct}%`) : null,
      weekKey: null,
    },
    { key: "open_value", label: "Open cart value", icon: <LuWallet style={{ ...ICON, color: "#B45309" }} />, color: "#B45309", value: s ? money(s.open_value) : null, weekKey: null },
    { key: "revenue", label: "Won value (quoted)", icon: <LuBanknote style={{ ...ICON, color: "#28A745" }} />, color: "#28A745", value: s ? money(s.won_value) : null, weekKey: "revenue" },
  ];

  return (
    <div style={{ marginBottom: 14 }}>
      <KpiTileGrid tiles={pipeline} />
      <KpiTileGrid tiles={attention} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <button
          type="button"
          onClick={toggleBreakdown}
          aria-expanded={showBreakdown}
          style={{ border: "none", background: "none", padding: "2px 0", fontSize: 12.5, color: "#6B7280", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          {showBreakdown ? <LuChevronUp /> : <LuChevronDown />}
          By source and by agent
        </button>
        {rightAction}
      </div>

      {showBreakdown && (
        <div style={{ marginTop: 8, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "14px 18px", display: "flex", gap: 28, flexWrap: "wrap" }}>
          <BreakdownTable
            title="By source"
            rows={s?.by_source || []}
            nameKey="source_type"
            labelFor={(r) => CHANNEL_LABELS[r.source_type] || r.source_type}
          />
          <BreakdownTable title="By agent" rows={s?.by_agent || []} nameKey="agent_id" labelFor={(r) => r.agent_name} />
        </div>
      )}
    </div>
  );
};

export default LeadKpiStrip;
